import React, { useEffect, useState } from 'react'
import { SketchPicker as ColorPicker, ColorResult } from 'react-color'
import { Button, Form, Icon, Popup } from 'semantic-ui-react'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import ValidationInput, { IInputDatalist } from '../../../../components/ValidationInput/ValidationInput'
import { _MIXEDCOLORS } from '../../../../constants/colors'
import { decimalNumberRegex } from '../../../../regex/fieldsValidationRegex'
import { ICustomer } from '../../types/project'
import { IWizardProject } from '../../types/wizardData'

const maxLength20ValidationRule = new RegExp("^.{0,20}$")
const invalidMaxLength20ValidationRuleMsg = 'Max number of characters exceeded. There are only 20 chars allowed.'
const maxLength21 = 21

interface Props {
  customers: ICustomer[]
  open: boolean
  data: IWizardProject
  wizardFinishedSteps: number[]
  setWizardFinishedSteps: (steps: number[]) => void
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  onClose: () => void
}

const WizardEnterBaseData = (props: Props): JSX.Element => {
  const {
    customers,
    open,
    data,
    wizardFinishedSteps,
    setWizardFinishedSteps,
    setWizardStep,
    setProjectWizardData,
    onClose
  } = props
  const [projectTitle, setProjectTitle] = useState(data.name)
  const [projectColor, setProjectColor] = useState(data.color ? data.color : '')
  const [budget, setBudget] = useState(data.type === 'sponsor' ? data.budget?.toString() : undefined)
  const [customer, setCustomer] = useState(data.type === 'service_provider' ? data.customer : undefined)
  const [category, setCategory] = useState(data.category)
  const [inputValues, setInputValues] = useState<string>('')
  const [showList, setShowList] = useState(false)

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * _MIXEDCOLORS.length);
    setProjectColor(data.color ? data.color : _MIXEDCOLORS[randomIndex])
  }, [data.color])

  useEffect(() => {
    setInputValues(data.budget ? data.budget.toString().replace('.', ',') : '')
  }, [data.budget])

  useEffect(() => {
    if (wizardFinishedSteps.includes(3)) {
      if (projectTitle === '' || projectTitle.length >= maxLength21) {
        setWizardFinishedSteps([1, 2])
      }
      if (data.type === 'sponsor' || data.type === 'internal') {
        if (category !== undefined && category.length >= maxLength21) {
          setWizardFinishedSteps([1, 2])
        }
      }
      if (data.type === 'sponsor') {
        if (budget) {
          if (!decimalNumberRegex.regexPattern.test(budget.toString())) {
            setWizardFinishedSteps([1, 2])
          }
        }
      } else if (data.type === 'service_provider') {
        if (customer === '' || customer === undefined || customer.length >= maxLength21) {
          setWizardFinishedSteps([1, 2])
        }
      }
    }
  }, [setWizardFinishedSteps, wizardFinishedSteps, projectTitle, category, data.type, budget, customer])

  const inputChange = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
    key: React.Dispatch<React.SetStateAction<string>> | React.Dispatch<React.SetStateAction<string | undefined>>,
    limit?: boolean) => {
    if (!limit) {
      key(event.target.value)
    } else if (/^.{0,30}$/.test(event.target.value)) {
      key(event.target.value)
    }
  }

  const budgetChange = (event: React.ChangeEvent<HTMLInputElement>, converted: number) => {
    if (/^\d+[.,]\d+$|^\d+/.test(converted.toString())) {
      setBudget(converted.toString())
      setInputValues(event.target.value)
    }
    if (event.target.value === '') {
      setBudget(undefined)
      setInputValues('')
    }
  }

  const renderInputs = (
    value: string | undefined,
    label: string,
    placeholder: string,
    key: React.Dispatch<React.SetStateAction<string>> | React.Dispatch<React.SetStateAction<string | undefined>>,
    symbol?: string) => {
    if (label === 'Budget') {
      return (
        <Form.Group>
          <ValidationInput
            icon='euro'
            iconPosition='left'
            fluid
            type='number'
            value={inputValues}
            onChange={(e, v, c) => budgetChange(e, c ?? 0)}
            placeholder={placeholder}
            label={
              <label style={{ display: 'flex', marginBottom: '.5rem', alignItems: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>{label}</p>
              </label>
            }
          />
        </Form.Group>
      )
    } else if (label === 'Client') {
      const _customers: string[] = []
      customers.forEach(c => {
        if (c && !_customers.includes(c.name)) {
          _customers.push(c.name)
        }
      })
      const datalist: IInputDatalist[] = _customers.map(dl => {
        return {
          value: dl
        }
      })
      return (
        <Form.Group>
          <div style={{ width: '100%' }}>
            <ValidationInput
              fluid
              onFocus={() => setShowList(true)}
              onBlur={() => setShowList(false)}
              value={customer ? customer : ''}
              onChange={e => inputChange(e, key)}
              placeholder={placeholder}
              validatePattern={maxLength20ValidationRule}
              invalidMessage={invalidMaxLength20ValidationRuleMsg}
              maxLength={maxLength21}
              label={
                <label style={{ display: 'flex', marginBottom: '.5rem', alignItems: 'center' }}>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>{label}</p>
                </label>
              }
            />
            {showList && !datalist.find(d => d.value.toLowerCase() == customer?.toLowerCase()) &&
              <ul style={{ border: '1px solid #d7d7d7', maxHeight: '88px', overflow: 'auto', width: '460px', padding: '12px', background: '#FFF', position: 'absolute', zIndex: 1, listStyleType: 'none', borderRadius: '4px', margin: '4px 0' }}>
                {datalist.filter(d => d.value.toLowerCase().includes(customer ? customer.toLowerCase() : '')).map((d, i) => {
                  return (
                    <li key={i} onMouseDown={() => setCustomer(d.value)} style={{ cursor: 'pointer', padding: '2px 0' }}>{d.value}</li>
                  )
                })}
              </ul>}
          </div>
        </Form.Group>
      )
    } else {
      return (
        <Form.Group>
          <ValidationInput
            fluid
            value={value ? value : ''}
            onChange={e => inputChange(e, key)}
            placeholder={placeholder}
            symbol={symbol}
            validatePattern={maxLength20ValidationRule}
            invalidMessage={invalidMaxLength20ValidationRuleMsg}
            maxLength={maxLength21}
            marginBottom='10px'
            label={
              <label style={{ display: 'flex', marginBottom: '.5rem', alignItems: 'center' }}>
                <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>{label}</p>
              </label>
            }
          />
          {label === 'Project title' ? renderPopup() : null}
        </Form.Group>
      )
    }
  }

  const changeColor = (color: ColorResult['hex']) => {
    setProjectColor(color)
  }

  const renderColorPicker = () => {
    return (
      <ColorPicker disableAlpha presetColors={_MIXEDCOLORS} color={projectColor} onChange={color => changeColor(color.hex)} />
    )
  }

  const renderPopup = () => {
    return (
      <Popup
        id='projectColor'
        basic
        style={{ marginLeft: '-1.5rem' }}
        trigger={
          <Icon
            name='square'
            size='large'
            style={{ zIndex: 1, color: projectColor !== '' ? projectColor : 'silver', position: 'relative', marginTop: '2.9rem', marginLeft: '-3rem' }}
          />}
        content={renderColorPicker()}
        on='click'
        position='bottom left'
        hideOnScroll
      />
    )
  }

  const renderContent = () => {
    return (
      <Form>
        {renderInputs(projectTitle, 'Project title', 'Enter project title', setProjectTitle)}
        {data.type !== 'service_provider' ? renderInputs(category, 'Type', 'Enter project type', setCategory) : null}
        {data.type === 'sponsor' ? renderInputs(budget ? budget.toString() : '', 'Budget', 'Enter available budget', setBudget, '€') : null}
        {data.type === 'service_provider' ? renderInputs(customer ? customer : '', 'Client', 'Enter client', setCustomer) : null}
      </Form>
    )
  }

  const disableNextButton = () => {
    if (projectTitle === '' || projectTitle.length >= maxLength21) {
      return true
    }

    if (data.type === 'sponsor' || data.type === 'internal') {
      return category !== undefined && category.length >= maxLength21
    }

    if (data.type === 'sponsor') {
      return budget ? !decimalNumberRegex.regexPattern.test(budget.toString()) : false
    } else if (data.type === 'service_provider') {
      return customer === '' || customer === undefined || customer.length >= maxLength21
    }
    return false
  }

  const renderFooter = () => {
    return (
      <div>
        <Button
          primary={true}
          size='small'
          onClick={goToNextStep}
          content='Next'
          disabled={disableNextButton()}
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, margin: 0 }}
        />
      </div>
    )
  }

  const goToNextStep = () => {
    const _data: IWizardProject = {
      ...data,
      name: projectTitle,
      color: projectColor !== '' ? projectColor : '#BEC8C3',
      // service_provider type
      customer: customer,
      // sponsor type
      budget: budget ? +budget : 0,
      category: category
    }
    if (!wizardFinishedSteps.includes(3)) {
      setWizardFinishedSteps([1, 2, 3])
    }
    setWizardStep('step-three')
    setProjectWizardData(_data)
  }

  const renderWizard = () => {
    const _data: IWizardProject = {
      ...data,
      name: projectTitle,
      color: projectColor !== '' ? projectColor : '#BEC8C3',
      // service_provider type
      customer: customer,
      // sponsor type
      budget: budget ? /^\d+\.\d+$|^\d+$/.test(budget ? budget.toString() : '') ? +budget : undefined : undefined,
    }
    const _steps = [
      { key: 1, name: 'step-one' },
      { key: 2, name: 'step-two' },
      { key: 3, name: 'step-three' },
      { key: 4, name: 'step-four' },
      { key: 5, name: 'step-five' },
      { key: 6, name: 'step-six' }
    ]
    return (
      <WizardComponent
        open={open}
        close={onClose}
        content={renderContent()}
        headerContent='Enter Base Data'
        headerColor='#232C58'
        footer={renderFooter()}
        steps={data.type === 'service_provider' ? 6 : 5}
        activeStep={2}
        width={'530px'}
        finishedSteps={wizardFinishedSteps}
        setWizardStep={setWizardStep}
        setProjectWizardData={setProjectWizardData}
        data={_data}
        stepStringList={_steps}
        headerContentFontSize='31.5px'
      />
    )
  }

  return renderWizard()
}

export default WizardEnterBaseData
